import React, { useState, useEffect, useRef } from 'react'
import { toast } from 'react-toastify'
import recallsService from '../../services/recalls.service'
import { useParams, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import Loading from "../common/Loading";

function RecallEdit() {
    const [recall, setRecall] = useState(null)
    const [isUploading, setIsUploading] = useState(false)
    const [isDragOver, setIsDragOver] = useState(false)
    const fileInputRef = useRef(null)
    const { recallId } = useParams()
    const { register, handleSubmit, reset, formState: { errors, isSubmitting }, watch, setValue } = useForm()

    const statusOptions = [
        { value: 'PENDING', label: 'Pending', color: 'warning' },
        { value: 'IN_PROCESS', label: 'In Process', color: 'info' },
        { value: 'COMPLETED', label: 'Completed', color: 'success' },
        { value: 'FAILED', label: 'Failed', color: 'danger' }
    ]

    useEffect(() => {
        getRecallById()
    }, [])

    const getRecallById = () => {
        recallsService.getRecallsById(recallId)
            .then(res => {
                setRecall(res.data)
                reset(res.data)
            })
            .catch(err => {
                console.log(err)
                toast.error("Failed to load recall details")
            })
    }

    const onSubmit = (data) => {
        recallsService.updateRecall(data.id, data)
            .then(res => {
                setRecall(res.data)
                toast.success("", {position: toast.POSITION.BOTTOM_RIGHT})
            })
            .catch(err => {
                toast.error("Error Updating Recall", {
                    position: toast.POSITION.BOTTOM_RIGHT
                })
                console.log(err)
            })
    }

    const handleFileSelect = (event) => {
        const file = event.target.files[0]
        if (file) {
            uploadFile(file)
        }
    }

    const handleDragOver = (e) => {
        e.preventDefault()
        setIsDragOver(true)
    }

    const handleDragLeave = (e) => {
        e.preventDefault()
        setIsDragOver(false)
    }

    const handleDrop = (e) => {
        e.preventDefault()
        setIsDragOver(false)

        const files = e.dataTransfer.files
        if (files.length > 0) {
            const file = files[0]
            uploadFile(file)
        }
    }

    const uploadFile = async (file) => {
        if (file.type !== 'text/csv' && !file.name.toLowerCase().endsWith('.csv')) {
            toast.error("Please upload a CSV file", {
                position: toast.POSITION.BOTTOM_RIGHT
            })
            return
        }

        setIsUploading(true)
        try {
            const formData = new FormData()
            formData.append('file', file)
            formData.append('recallId', recallId)

            const response = await recallsService.uploadRecallFile(recallId, formData)
            setRecall(prev => ({ ...prev, filePath: response.data.filePath }))
            toast.success("File uploaded successfully", {
                position: toast.POSITION.BOTTOM_RIGHT
            })
        } catch (error) {
            toast.error("Error uploading file", {
                position: toast.POSITION.BOTTOM_RIGHT
            })
            console.error(error)
        } finally {
            setIsUploading(false)
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = ''
            }
        }
    }

    const processString = (str) => {
        let processedStr = str.toLowerCase().replace(/ /g, '_')
        const timestamp = new Date().getTime()
        return `${processedStr}_${timestamp}`
    }

    const handleDownload = (fileurl, id, clinicName) => {
        recallsService.fileDownload(fileurl)
            .then(response => {
                const fileName = processString(clinicName + id)
                const blob = new Blob([response.data], { type: response.headers['content-type'] })
                const url = window.URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.download = `${fileName}.csv`
                link.click()
                window.URL.revokeObjectURL(url)
            })
            .catch(err => {
                console.log(err)
                toast.error("Error downloading file")
            })
    }

    const StatusBadge = ({ status }) => {
        const statusOption = statusOptions.find(opt => opt.value === status)
        return (
            <span className={`badge bg-${statusOption?.color || 'secondary'}`}>
                {statusOption?.label || status}
            </span>
        )
    }

    const triggerFileInput = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click()
        }
    }

    if (!recall) {
        return (
            <Loading />
        )
    }

    return (
        <div className="container container-xxl">
            <div className="pagetitle">
                <h1><i className="bi bi-repeat"></i> Recall Request</h1>
            </div>

            <div className="card card-padding card-shadow">
                <div className="card-title mb-0">
                    <i className="bi bi-pencil-square me-2"></i>
                    Edit Recall Details
                </div>
                <hr/>
                <div className="card-body">
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="row">
                            {/* Left Column - Read-only Details */}
                            <div className="col-lg-6">
                                <div className="mb-4">
                                    <h6 className="text-primary mb-3">
                                        <i className="bi bi-info-circle me-2"></i>
                                        Recall Information
                                    </h6>
                                    <div className="row">
                                        <div className="col-12 mb-3">
                                            <label className="form-label fw-semibold text-muted small mb-1">Clinic Name</label>
                                            <div className="form-control bg-light">{recall.clinicName}</div>
                                        </div>
                                        <div className="col-12 mb-3">
                                            <label className="form-label fw-semibold text-muted small mb-1">Title</label>
                                            <div className="form-control bg-light">{recall.title}</div>
                                        </div>
                                        <div className="col-12 mb-3">
                                            <label className="form-label fw-semibold text-muted small mb-1">Notes</label>
                                            <div className="form-control bg-light" style={{ minHeight: '100px' }}>
                                                {recall.notes || 'No notes provided'}
                                            </div>
                                        </div>
                                        <div className="col-12 mb-3">
                                            <label className="form-label fw-semibold text-muted small mb-1">Current Status</label>
                                            <div className="form-control bg-light">
                                                <StatusBadge status={recall.status} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right Column - Editable Fields */}
                            <div className="col-lg-6">
                                <div className="mb-4">
                                    <h6 className="text-primary mb-3">
                                        <i className="bi bi-gear me-2"></i>
                                        Update Settings
                                    </h6>
                                    <div className="row">
                                        <div className="col-12 mb-3">
                                            <label className="form-label fw-semibold">Status</label>
                                            <select
                                                className={`form-select ${errors.status ? 'is-invalid' : ''}`}
                                                {...register('status', { required: "Status is required" })}
                                            >
                                                <option value="">Select Status</option>
                                                {statusOptions.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.status && (
                                                <div className="invalid-feedback">{errors.status.message}</div>
                                            )}
                                        </div>

                                        <div className="col-12 mb-3">
                                            <label className="form-label fw-semibold">Admin Notes</label>
                                            <textarea
                                                rows="4"
                                                className={`form-control ${errors.adminNotes ? 'is-invalid' : ''}`}
                                                placeholder="Add admin notes here..."
                                                {...register('adminNotes')}
                                            />
                                            {errors.adminNotes && (
                                                <div className="invalid-feedback">{errors.adminNotes.message}</div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* File Upload Section */}
                                <div className="mb-4">
                                    <h6 className="text-primary mb-3">
                                        <i className="bi bi-file-earmark-arrow-up me-2"></i>
                                        File Management
                                    </h6>
                                    <div className="row">
                                        <div className="col-12 mb-3">
                                            <label className="form-label fw-semibold">Current File</label>
                                            {recall.filePath ? (
                                                <div className="d-flex align-items-center justify-content-between p-3 border rounded bg-light">
                                                    <div>
                                                        <i className="bi bi-file-earmark-text me-2"></i>
                                                        {recall.filePath.split('/').pop()}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-primary"
                                                        onClick={() => handleDownload(recall.filePath, recall.id, recall.clinicName)}
                                                    >
                                                        <i className="bi bi-download me-1"></i>
                                                        Download
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="text-muted p-3 border rounded bg-light">
                                                    No file uploaded
                                                </div>
                                            )}
                                        </div>

                                        <div className="col-12">
                                            <label className="form-label fw-semibold">Upload New File</label>
                                            <input
                                                type="file"
                                                ref={fileInputRef}
                                                onChange={handleFileSelect}
                                                accept=".csv"
                                                className="d-none"
                                                disabled={isUploading}
                                            />
                                            <div
                                                className={`border-dashed border-2 rounded-3 p-4 text-center ${
                                                    isDragOver ? 'border-primary bg-primary bg-opacity-10' : 'border-secondary'
                                                } ${isUploading ? 'opacity-50' : 'cursor-pointer'}`}
                                                style={{
                                                    cursor: isUploading ? 'not-allowed' : 'pointer',
                                                    transition: 'all 0.2s ease'
                                                }}
                                                onClick={isUploading ? null : triggerFileInput}
                                                onDragOver={handleDragOver}
                                                onDragLeave={handleDragLeave}
                                                onDrop={handleDrop}
                                            >
                                                {isUploading ? (
                                                    <div>
                                                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                                                            <span className="visually-hidden">Uploading...</span>
                                                        </div>
                                                        Uploading...
                                                    </div>
                                                ) : isDragOver ? (
                                                    <div>
                                                        <i className="bi bi-cloud-arrow-up fs-1 text-primary"></i>
                                                        <p className="mt-2 mb-0">Drop the CSV file here</p>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <i className="bi bi-cloud-arrow-up fs-1 text-muted"></i>
                                                        <p className="mt-2 mb-1">Drag & drop a CSV file here</p>
                                                        <p className="text-muted small mb-0">or click to browse</p>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="form-text">
                                                Only CSV files are accepted. This will replace the current file.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row mt-4">
                            <div className="col-12">
                                <div className="d-flex justify-content-between align-items-center gap-2">
                                    <Link to="/recalls" className="btn btn-outline-secondary btn-sm">
                                        <i className="bi bi-arrow-left me-1"></i>
                                        Back to List
                                    </Link>
                                    <button
                                        type="submit"
                                        className="btn btn-success btn-sm"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Updating...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-check-circle me-1"></i>
                                                Update Recall
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}

export default RecallEdit